import { z } from 'zod';
import { bulkCreateOrganizations } from 'wasp/client/operations';
import type { CsvEntityConfig } from '../types';

const validationSchema = z.object({
  oid: z.string().optional().default(''),
  name: z.string().min(1, 'Name is required'),
  slug: z.string().min(1, 'Slug is required'),
  organizationTypeKey: z.string().min(1, 'Organization Type Key is required'),
  parentOrganizationSlug: z.string().optional().default(''),
  specialization: z.string().optional().default(''),
  scope: z.enum(['NATIONAL', 'PROVINCIAL', 'MUNICIPAL']).optional(),
  provinceCode: z.string().optional().default(''),
  cityName: z.string().optional().default(''),
  address: z.string().optional().default(''),
  phone: z.string().optional().default(''),
  contact: z.string().optional().default(''),
  email: z.string().optional().default(''),
  website: z.string().optional().default(''),
  description: z.string().optional().default(''),
  languages: z.string().optional().default(''), // comma-separated, e.g. "EN,FR"
  isActive: z.string().optional().default('true'),
});

export const organizationConfig: CsvEntityConfig = {
  entityName: 'Organizations',
  entityNameSingular: 'Organization',
  columns: [
    { key: 'oid', label: 'OID', required: false, description: 'User-assigned organization ID (e.g., ORG-001)', exampleValue: 'ORG-001' },
    { key: 'name', label: 'Name', required: true, description: 'Organization name', exampleValue: 'Central Center' },
    { key: 'slug', label: 'Slug', required: true, description: 'Unique URL slug', exampleValue: 'central-Center' },
    { key: 'organizationTypeKey', label: 'Org Type Key', required: true, description: 'Key of existing organization type', exampleValue: 'Center' },
    { key: 'parentOrganizationSlug', label: 'Parent Org Slug', required: false, description: 'Slug of parent organization', exampleValue: '' },
    { key: 'specialization', label: 'Specialization', required: false, description: 'Specialization or sub-type text', exampleValue: 'Youth Programs' },
    { key: 'scope', label: 'Scope', required: false, description: 'NATIONAL, PROVINCIAL, or MUNICIPAL', exampleValue: 'MUNICIPAL' },
    { key: 'provinceCode', label: 'Province Code', required: false, description: 'Province code (e.g., ON, BC, QC)', exampleValue: 'ON' },
    { key: 'cityName', label: 'City Name', required: false, description: 'Municipality name — must match existing record', exampleValue: 'Toronto' },
    { key: 'address', label: 'Address', required: false, description: 'Street address', exampleValue: '123 Main St' },
    { key: 'phone', label: 'Phone', required: false, description: 'Phone number', exampleValue: '416-555-0100' },
    { key: 'contact', label: 'Contact', required: false, description: 'Contact person name', exampleValue: 'Ahmed Hassan' },
    { key: 'email', label: 'Email', required: false, description: 'Contact email', exampleValue: 'info@centralCenter.ca' },
    { key: 'website', label: 'Website', required: false, description: 'Website URL', exampleValue: 'https://centralCenter.ca' },
    { key: 'description', label: 'Description', required: false, description: 'Optional description', exampleValue: 'Main community Center' },
    { key: 'languages', label: 'Languages', required: false, description: 'Comma-separated language codes (e.g., EN,FR,AR)', exampleValue: 'EN,AR' },
    { key: 'isActive', label: 'Is Active', required: false, description: 'true/false (default: true)', exampleValue: 'true' },
  ],
  validationSchema,
  exampleRows: [
    {
      oid: 'ORG-001',
      name: 'Central Center',
      slug: 'central-Center',
      organizationTypeKey: 'Center',
      parentOrganizationSlug: '',
      specialization: '',
      scope: 'MUNICIPAL',
      provinceCode: 'ON',
      cityName: 'Toronto',
      address: '123 Main St',
      phone: '416-555-0100',
      contact: 'Ahmed Hassan',
      email: 'info@centralCenter.ca',
      website: 'https://centralCenter.ca',
      description: 'Main community Center',
      languages: 'EN,AR',
      isActive: 'true',
    },
    {
      oid: 'ORG-002',
      name: 'Al-Huda School',
      slug: 'al-huda-school',
      organizationTypeKey: 'school',
      parentOrganizationSlug: '',
      specialization: 'Community Education',
      scope: 'PROVINCIAL',
      provinceCode: 'ON',
      cityName: 'Mississauga',
      address: '',
      phone: '',
      contact: '',
      email: '',
      website: '',
      description: 'Community school',
      languages: 'EN,FR',
      isActive: 'true',
    },
  ],
  bulkCreateAction: bulkCreateOrganizations as any,
  importNotes: [
    'Organization Type Key must match an existing organization type.',
    'Parent Organization Slug must reference an existing organization.',
    'Province Code must match an existing CanadaProvince (e.g., ON, BC, QC).',
    'City Name must match an existing CanadaMunicipality within the specified province.',
    'Languages: comma-separated codes, e.g. EN,FR,AR',
    'Scope values: NATIONAL, PROVINCIAL, MUNICIPAL',
  ],
};



